"use client";

import { useEffect, useState } from "react";
import { readChatwootConfig, type ChatwootConfig } from "@/lib/chatwoot";

const POLL_INTERVAL_MS = 100;
const POLL_TIMEOUT_MS = 10_000;

/**
 * Resolves Chatwoot config once env-config.js has executed, retrying instead
 * of sampling once — window.__ENV__ isn't guaranteed to exist on first paint.
 * Returns null while unresolved/unconfigured (e.g. preview deploys with no
 * chat channel), so callers can skip rendering chat-dependent UI entirely.
 */
export function useChatwootConfig(): ChatwootConfig | null {
  // Lazy initializer, not a setState-in-effect: env-config.js runs
  // `beforeInteractive`, so on the client this usually resolves immediately.
  const [config, setConfig] = useState<ChatwootConfig | null>(() => readChatwootConfig());

  useEffect(() => {
    if (config) return;

    let waited = 0;
    const handle = window.setInterval(() => {
      const next = readChatwootConfig();
      if (next) {
        window.clearInterval(handle);
        setConfig(next);
        return;
      }
      waited += POLL_INTERVAL_MS;
      if (waited >= POLL_TIMEOUT_MS) window.clearInterval(handle);
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(handle);
  }, [config]);

  return config;
}
